// src/components/homeComponents/FeedTest.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  FaHeart,
  FaCommentDots,
  FaPaperPlane,
  FaBookmark,
} from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

const PUB = process.env.PUBLIC_URL || "";
const design = (name) => `${PUB}/designs/${encodeURIComponent(name)}`;

/*
  THE FEED TEST — you scroll the page, you scroll the phone.
  Each post is a real ASites creative. Likes count up when a post lands,
  hearts burst, the room light changes to the post's color, and a
  "director's commentary" caption floats in beside the phone.
  NOTE for Amiri: swap likes/handles for real numbers when you have them.
*/
const POSTS = [
  {
    id: 0,
    image: design("Orhidea Perfume.png"),
    handle: "@orhidea.perfume",
    likes: 48200,
    accent: "#c98a4b",
    note: "Warm light, product as the hero.",
    caption: "Hook lands in the first 0.4 seconds.",
    side: "left",
  },
  {
    id: 1,
    image: design("Pretty Needs.png"),
    handle: "@prettyneeds",
    likes: 96400,
    accent: "#e6a3b3",
    note: "Pain point → product, one frame.",
    caption: "Saved and sent more than it was liked.",
    side: "right",
  },
  {
    id: 2,
    image: design("RRUSH.png"),
    handle: "@rrush.drinks",
    likes: 132000,
    accent: "#f2b134",
    note: "Motion frozen mid-splash.",
    caption: "The thumb physically stops here.",
    side: "left",
  },
  {
    id: 3,
    image: design("CORFU.png"),
    handle: "@travelo.ks",
    likes: 61800,
    accent: "#4ea9d6",
    note: "Price + place + urgency.",
    caption: "Bookings before the season opened.",
    side: "right",
  },
  {
    id: 4,
    image: design("Kur Stili yt.png"),
    handle: "@jaraboutique",
    likes: 27400,
    accent: "#b9a58c",
    note: "Editorial, not catalogue.",
    caption: "A boutique that posts like a magazine.",
    side: "left",
  },
];

const fmt = (n) =>
  n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K` : `${n}`;

const FeedTest = () => {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const feedRef = useRef(null);
  const phoneRef = useRef(null);
  const auraRef = useRef(null);
  const likeRefs = useRef([]);
  const burstRefs = useRef([]);
  const likedFlags = useRef({});
  const [active, setActive] = useState(0);

  const fireLikes = (i) => {
    if (likedFlags.current[i]) return;
    likedFlags.current[i] = true;

    // like counter roll-up
    const node = likeRefs.current[i];
    if (node) {
      const obj = { v: Math.max(POSTS[i].likes - 1800, 0) };
      gsap.to(obj, {
        v: POSTS[i].likes,
        duration: 1.6,
        ease: "power3.out",
        onUpdate: () => {
          node.textContent = fmt(Math.round(obj.v));
        },
      });
    }

    // heart burst
    const burst = burstRefs.current[i];
    if (burst) {
      const hearts = burst.querySelectorAll(".b-heart");
      hearts.forEach((h) => {
        gsap.fromTo(
          h,
          { opacity: 1, scale: 0.4, x: 0, y: 0, rotate: 0 },
          {
            opacity: 0,
            scale: 1.3,
            x: gsap.utils.random(-70, 70),
            y: gsap.utils.random(-130, -40),
            rotate: gsap.utils.random(-40, 40),
            duration: gsap.utils.random(0.9, 1.5),
            ease: "power2.out",
          }
        );
      });
    }
  };

  useEffect(() => {
    const section = sectionRef.current;
    const pin = pinRef.current;
    const feed = feedRef.current;
    const phone = phoneRef.current;
    if (!section || !pin || !feed || !phone) return;

    let st;
    let lastP = 0;

    const t = setTimeout(() => {
      const step = feed.clientHeight / POSTS.length; // one screen per post
      const distance = step * (POSTS.length - 1);

      st = ScrollTrigger.create({
        trigger: section,
        start: "top top",
        end: `+=${POSTS.length * 90}%`,
        pin: pin,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const p = self.progress;

          // feed scrolls inside the phone
          gsap.set(feed, { y: -p * distance });

          // phone tilts slightly with scroll direction (velocity feel)
          const vel = gsap.utils.clamp(-1, 1, (p - lastP) * 260);
          lastP = p;
          gsap.to(phone, {
            rotateX: vel * -4,
            rotateZ: vel * 1.2,
            duration: 0.5,
            ease: "power2.out",
            overwrite: "auto",
          });

          // active post
          const idx = Math.min(
            POSTS.length - 1,
            Math.round(p * (POSTS.length - 1))
          );
          setActive(idx);
          fireLikes(idx);

          // room light follows the post
          if (auraRef.current) {
            gsap.to(auraRef.current, {
              background: `radial-gradient(circle at 50% 45%, ${POSTS[idx].accent}33 0%, #08040c 60%)`,
              duration: 0.8,
              overwrite: "auto",
            });
          }
        },
      });
      ScrollTrigger.refresh();
    }, 120);

    return () => {
      clearTimeout(t);
      if (st) st.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Section ref={sectionRef}>
      <Pin ref={pinRef}>
        <div className="aura" ref={auraRef} />

        <div className="head">
          <span className="kicker">THE FEED TEST</span>
          <h2>
            Scroll. <em>We dare you.</em>
          </h2>
        </div>

        <Stage>
          {/* floating director's commentary */}
          {POSTS.map((p, i) => (
            <Caption
              key={p.id}
              $side={p.side}
              $active={i === active}
              style={{ "--accent": p.accent }}
            >
              <span className="tick" />
              <span className="cap">{p.caption}</span>
              <span className="sub">{p.note}</span>
            </Caption>
          ))}

          <Phone ref={phoneRef}>
            <div className="notch" />
            <div className="screen">
              <div className="feed" ref={feedRef}>
                {POSTS.map((p, i) => (
                  <div className="post" key={p.id}>
                    <img src={p.image} alt={p.handle} loading="lazy" />
                    <div className="shade" />

                    <div className="post-ui">
                      <div className="handle">
                        <span
                          className="avatar"
                          style={{ background: p.accent }}
                        />
                        {p.handle}
                        <em>· Follow</em>
                      </div>

                      <div className="rail">
                        <div className="rail-item">
                          <FaHeart
                            className="ico heart"
                            style={{
                              color: i === active ? "#ff4d6d" : "#ffffff",
                            }}
                          />
                          <span
                            className="count"
                            ref={(el) => (likeRefs.current[i] = el)}
                          >
                            {fmt(Math.max(p.likes - 1800, 0))}
                          </span>
                        </div>
                        <div className="rail-item">
                          <FaCommentDots className="ico" />
                          <span className="count">{fmt(Math.round(p.likes / 38))}</span>
                        </div>
                        <div className="rail-item">
                          <FaPaperPlane className="ico" />
                          <span className="count">{fmt(Math.round(p.likes / 21))}</span>
                        </div>
                        <div className="rail-item">
                          <FaBookmark className="ico" />
                        </div>
                      </div>

                      <div
                        className="burst"
                        ref={(el) => (burstRefs.current[i] = el)}
                      >
                        {Array.from({ length: 8 }).map((_, k) => (
                          <FaHeart className="b-heart" key={k} />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Phone>

          <Dots>
            {POSTS.map((p, i) => (
              <span
                key={p.id}
                className={i === active ? "on" : ""}
                style={{ background: i === active ? p.accent : undefined }}
              />
            ))}
          </Dots>
        </Stage>

        <div className="foot">
          This is what we make for clients. Your feed could look like this.
        </div>
      </Pin>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  background: #08040c;
`;

const Pin = styled.div`
  height: 100vh;
  height: 100dvh;
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .aura {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 45%, #33224d33 0%, #08040c 60%);
    pointer-events: none;
  }

  .head {
    position: absolute;
    top: 6vh;
    left: 7%;
    z-index: 6;
    pointer-events: none;
    .kicker {
      font-family: "Montserrat";
      letter-spacing: 0.45em;
      font-size: 0.72rem;
      color: #836caf;
      font-weight: 500;
    }
    h2 {
      font-family: "Readex Pro", sans-serif;
      font-weight: 200;
      font-size: clamp(1.8rem, 4vw, 3.6rem);
      line-height: 1;
      margin-top: 0.35em;
      em {
        font-style: italic;
        color: #9281dd;
      }
    }
  }

  .foot {
    position: absolute;
    bottom: 4.5vh;
    z-index: 6;
    color: #8a8595;
    font-size: 0.85rem;
    letter-spacing: 0.04em;
    text-align: center;
    padding: 0 7%;
  }

  @media (max-width: 700px) {
    .head {
      top: 4vh;
      h2 {
        font-size: clamp(1.4rem, 6vw, 1.9rem);
      }
    }
    .foot {
      bottom: 2.2vh;
      font-size: 0.72rem;
    }
  }
`;

const Stage = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  perspective: 1200px;
`;

const Phone = styled.div`
  position: relative;
  width: min(320px, 72vw, calc((100vh - 250px) * 9 / 19));
  width: min(320px, 72vw, calc((100dvh - 250px) * 9 / 19));
  aspect-ratio: 9 / 19;
  border-radius: 44px;
  background: #0d0a14;
  border: 1px solid #2a2140;
  box-shadow: 0 0 0 10px #120d1d, 0 60px 120px -40px #000,
    inset 0 0 30px #00000088;
  transform-style: preserve-3d;
  will-change: transform;

  .notch {
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 34%;
    height: 22px;
    background: #08060e;
    border-radius: 20px;
    z-index: 4;
  }

  .screen {
    position: absolute;
    inset: 8px;
    border-radius: 36px;
    overflow: hidden;
    background: #000;
  }

  .feed {
    height: 500%; /* POSTS.length * 100% */
    will-change: transform;
  }

  .post {
    position: relative;
    height: 20%; /* 100 / POSTS.length */
    width: 100%;
    overflow: hidden;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .shade {
      position: absolute;
      inset: 0;
      background: linear-gradient(
        180deg,
        #00000055 0%,
        transparent 25%,
        transparent 60%,
        #000000aa 100%
      );
    }
  }

  .post-ui {
    position: absolute;
    inset: 0;
    font-family: "Montserrat";

    .handle {
      position: absolute;
      top: 14px;
      left: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.72rem;
      font-weight: 600;
      color: #fff;
      text-shadow: 0 1px 6px #000;
      .avatar {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 2px solid #fff;
        display: inline-block;
      }
      em {
        font-style: normal;
        font-weight: 400;
        color: #ffffffbb;
      }
    }

    .rail {
      position: absolute;
      right: 10px;
      bottom: 18px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      align-items: center;
      .rail-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 3px;
        .ico {
          font-size: 1.25rem;
          color: #fff;
          filter: drop-shadow(0 1px 4px #000);
          transition: color 0.4s ease, transform 0.4s ease;
        }
        .heart {
          transform-origin: center;
        }
        .count {
          font-size: 0.62rem;
          color: #fff;
          font-weight: 600;
          text-shadow: 0 1px 4px #000;
        }
      }
    }

    .burst {
      position: absolute;
      right: 16px;
      bottom: 130px;
      width: 0;
      height: 0;
      pointer-events: none;
      .b-heart {
        position: absolute;
        color: #ff4d6d;
        font-size: 1rem;
        opacity: 0;
      }
    }
  }
`;

const Caption = styled.div`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === "left" ? "right: 58%;" : "left: 58%;")}
  transform: translateY(-50%)
    translateX(${({ $active, $side }) =>
      $active ? "0" : $side === "left" ? "-24px" : "24px"});
  max-width: 240px;
  text-align: ${({ $side }) => ($side === "left" ? "right" : "left")};
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.6s ease, transform 0.6s ease;
  z-index: 5;
  pointer-events: none;

  .tick {
    display: block;
    width: 34px;
    height: 1px;
    background: var(--accent);
    margin: ${({ $side }) =>
      $side === "left" ? "0 0 10px auto" : "0 auto 10px 0"};
  }
  .cap {
    display: block;
    font-family: "Readex Pro", sans-serif;
    font-weight: 300;
    font-size: clamp(1rem, 1.5vw, 1.3rem);
    color: #ffffff;
    line-height: 1.3;
  }
  .sub {
    display: block;
    margin-top: 0.5em;
    font-size: 0.78rem;
    color: #8a8595;
    letter-spacing: 0.03em;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const Dots = styled.div`
  position: absolute;
  left: calc(50% + min(200px, 44vw));
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 5;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #2c2342;
    transition: 0.4s;
    &.on {
      height: 22px;
      border-radius: 4px;
    }
  }
  @media (max-width: 900px) {
    left: auto;
    right: 6%;
  }
`;

export default FeedTest;
